import React, { ReactNode, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { getBackgroundColor } from '../../utils';
import { Button } from '../button';
import { Close } from '../icon';

const StyledModal = styled.dialog`
  ${({ theme: { maxMedia, media } }) => css`
    border: 0;
    max-height: 100dvh;
    max-width: 100vw;
    overscroll-behavior: contain;

    @media (max-width: ${maxMedia.medium}) {
      height: 100dvh;
      width: 100vw;
    }

    @media (min-width: ${media.medium}) {
      max-height: calc(100dvh - 2rem);
      max-width: 736px;
      margin: auto;
    }

    &::backdrop {
      ${getBackgroundColor('neutralOpacity')}
      overflow: hidden; /* Needed to make the backdrop a scroll container */
      overscroll-behavior: contain;
    }

    /* Override form max-width so it fills modal */
    form {
      max-width: unset;
    }
  `};
`;

const StyledModalInner = styled.div`
  ${({ theme: { media, space } }) => css`
    ${getBackgroundColor('neutralWhite')}
    padding: ${space.medium};
    padding-top: ${space.large};

    @media (min-width: ${media.medium}) {
      padding-top: ${space.xLarge};
      padding-inline: ${space.large};
    }
  `};
`;

const CloseModalButton = styled(Button)`
  ${({ theme: { media, space } }) => css`
    position: absolute;
    right: ${space.small};
    top: ${space.xSmall};

    @media (min-width: ${media.medium}) {
      top: ${space.small};
    }
  `};
`;

export const Modal = ({
  children,
  isOpen,
  onClose,
}: {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isOpen && !dialog.open) {
      dialog.showModal();
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  return (
    <StyledModal
      ref={dialogRef}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
      onClose={onClose}
    >
      <CloseModalButton
        variant="primaryInverse"
        iconRight={<Close size="small" />}
        a11yTitle="Close"
        onClick={onClose}
        small
      />
      <StyledModalInner>{children}</StyledModalInner>
    </StyledModal>
  );
};

export default Modal;
